import classNames from "classnames";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { BiCheck } from "react-icons/bi";
import {
  MdErrorOutline,
  MdOutlineCategory,
  MdOutlineCheckCircle,
  MdOutlineTableRows,
} from "react-icons/md";
import Button from "shared/components/Button";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import { resolveError } from "./constants";
import StepCard from "./StepCard";

const StepValidation = ({ validationResult, onNext, onBack, backLabel }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const IMPORT = t("IMPORT", { returnObjects: true });

  const isValid = get(validationResult, "valid", false);
  const errors = get(validationResult, "errors", []);
  const warnings = get(validationResult, "warnings", []);
  const preview = get(validationResult, "preview", {});
  const totalRows = get(validationResult, "totalRows", 0);

  const plans = get(preview, "plans", []);
  const products = get(preview, "products", []);
  const identifierCount = plans.filter((p) => p.identifier).length;

  const nextButton = (
    <Button type="primary" onClick={onNext} disabled={!isValid}>
      <BiCheck size={20} />
      {IMPORT.VALIDATION.NEXT}
    </Button>
  );

  return (
    <StepCard
      title={IMPORT.VALIDATION.TITLE}
      onBack={onBack}
      backLabel={backLabel}
      actions={nextButton}
    >
      <div
        className={classNames("pricers-import__status-banner", {
          "pricers-import__status-banner--success": isValid,
          "pricers-import__status-banner--error": !isValid,
        })}
      >
        {isValid ? (
          <MdOutlineCheckCircle size={24} color={COLORS.C_SUCCESS} />
        ) : (
          <MdErrorOutline size={24} color={COLORS.C_DANGER} />
        )}
        <div>
          <strong>
            {isValid
              ? IMPORT.VALIDATION.SUCCESS
              : `${errors.length} ${IMPORT.VALIDATION.ERRORS_FOUND}`}
          </strong>
          <p className="pricers-import__status-meta">
            {totalRows} {IMPORT.VALIDATION.ROWS_ANALYZED}
          </p>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="pricers-import__error-section">
          <h4 className="pricers-import__error-title">
            {IMPORT.VALIDATION.ERROR_DETAILS}
          </h4>
          <div className="pricers-import__error-list">
            {errors.slice(0, 30).map((err, i) => (
              <div key={i} className="pricers-import__error-item">
                <MdErrorOutline size={16} />
                {resolveError(err, IMPORT.ERROR_KEYS)}
              </div>
            ))}
          </div>
        </div>
      )}

      {warnings.length > 0 && (
        <div className="mb-24">
          {warnings.map((warn, i) => (
            <div key={i} className="pricers-import__warning-item">
              {typeof warn === "string" ? warn : JSON.stringify(warn)}
            </div>
          ))}
        </div>
      )}

      <div className="grid--3 gap-16 mb-24">
        <div className="pricers-import__stat-card">
          <MdOutlineCategory size={22} className="pricers-import__stat-icon" />
          <div className="pricers-import__stat-value">{plans.length}</div>
          <div className="pricers-import__stat-label">
            {IMPORT.VALIDATION.PLANS_DETECTED}
          </div>
        </div>
        <div className="pricers-import__stat-card">
          <MdOutlineTableRows
            size={22}
            className="pricers-import__stat-icon"
          />
          <div className="pricers-import__stat-value">{products.length}</div>
          <div className="pricers-import__stat-label">
            {IMPORT.VALIDATION.PRODUCTS}
          </div>
        </div>
        <div className="pricers-import__stat-card">
          <MdOutlineCategory size={22} className="pricers-import__stat-icon" />
          <div className="pricers-import__stat-value">{identifierCount}</div>
          <div className="pricers-import__stat-label">
            {IMPORT.VALIDATION.IDENTIFIERS}
          </div>
        </div>
      </div>

      {plans.length > 0 && (
        <div className="pricers-import__product-list">
          <h4 className="m-bottom-12">{IMPORT.VALIDATION.PLAN_TABLE}</h4>
          <div className="pricers-import__product-grid">
            {plans.slice(0, 50).map((p, i) => (
              <div key={i} className="pricers-import__product-item">
                <div className="pricers-import__product-name">{p.name}</div>
                <div className="pricers-import__product-meta">
                  {p.product && (
                    <span className="pricers-import__product-tag">
                      {p.product}
                    </span>
                  )}
                  {p.identifier && (
                    <span className="pricers-import__product-rows">
                      {p.identifier}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </StepCard>
  );
};

export default StepValidation;
